"use client"

import { getStripePromise } from "@lib/stripe"
import {
  Elements,
  PaymentMethodMessagingElement,
} from "@stripe/react-stripe-js"
import React from "react"

type FinancingMessageProps = {
  /** Amount in dollars (major units, as Medusa v2 stores it) */
  amount: number
  className?: string
}

type ErrorBoundaryState = {
  hasError: boolean
}

/**
 * A Stripe hiccup in the messaging element must never take the page down —
 * financing marketing is strictly best-effort.
 */
class FinancingErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return null
    }

    return this.props.children
  }
}

/**
 * Compliant "pay over time with Klarna / Affirm" marketing line supplied by
 * Stripe's PaymentMethodMessagingElement. Renders nothing when Stripe isn't
 * configured or the amount isn't positive.
 */
const FinancingMessage = ({ amount, className }: FinancingMessageProps) => {
  const stripePromise = getStripePromise()

  if (!stripePromise || !(amount > 0)) {
    return null
  }

  return (
    <div className={className} data-testid="financing-message">
      <FinancingErrorBoundary>
        <Elements stripe={stripePromise}>
          <PaymentMethodMessagingElement
            options={{
              amount: Math.round(amount * 100),
              currency: "USD",
              countryCode: "US",
              paymentMethodTypes: ["klarna", "affirm"],
            }}
          />
        </Elements>
      </FinancingErrorBoundary>
    </div>
  )
}

export default FinancingMessage
